import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';
import { useTranslations } from 'next-intl';
import { useEffect, useRef } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { v4 as uuid } from 'uuid';

import { postOneWayTpslApi, postOrderApi } from '@flipster/apis/v2/trade';
import { calcPositionEstUnrealizedPnl } from '@flipster/features/src/shared/calculator/tp-sl-preview';
import { orderAttributionAtom } from '@flipster/features/src/shared/order-attribution';
import { useHandleSubmit } from '@flipster/features/src/shared/react-hook-form/use-handle-submit';
import { TpSlTriggerDirectionMapper } from '@flipster/features/src/tpsl-modal/constant';
import { useDeleteTpSlLegacy } from '@flipster/features/src/tpsl-modal/use-delete-tp-sl';
import { accountBunja } from '@flipster/r230522/original/account';
import { openPositionsBunja } from '@flipster/r230522/original/position';
import { refPositionTimes } from '@flipster/r230522/original/server-time';
import { getTickerDisplayPriceOrDefault } from '@flipster/r230522/original/ticker-price';
import { snackbar } from '@flipster/react/design-system/snackbar';
import { dateToEpochString } from '@flipster/utils/datetime';

import { TpslSchema } from '@web/features/modals/TimeTriggerTpslModal/bunja/validation';
import { useTimeTriggerTpslBaseInfo } from '@web/features/modals/TimeTriggerTpslModal/useTimeTriggerTpslBaseInfo';

import { helperBunja } from './bunja/helper';
import LoadingSpinner from './components/LoadingSpinner';
import SymbolInfo from './components/SymbolInfo';
import ConfirmButton from './components/trigger-price/ConfirmButton';
import PriceInfos from './components/trigger-price/PriceInfos';
import TriggerPriceInput from './components/trigger-price/TriggerPriceInput';
import TriggerPriceRate from './components/trigger-price/TriggerPriceRate';
import { tpslModalSegmentBunja } from './tpsl-modal-bunja';
import { useTimeTriggerTpslModal } from './useTimeTriggerTpslModal';

const OpenPosition = () => {
  const transPosition = useTranslations('Position.r250401');
  const transAssets = useTranslations('Assets.r250401');
  const { checkShowLiquidationAlert, liquidationAlert, deleteTpSlAlert, tpSlErrorHandler } = useBunja(helperBunja);
  const initPosition = useRef<boolean>(false);
  const { control, handleSubmit, setValue, reset, trigger } = useFormContext<TpslSchema>();

  const { openPositionsAtom } = useBunja(openPositionsBunja);
  const positions = useAtomValue(openPositionsAtom);
  const { v2AccountAtom } = useBunja(accountBunja);
  const account = useAtomValue(v2AccountAtom);
  const attribution = useAtomValue(orderAttributionAtom);

  const currentTriggerPrice = useWatch({ control, name: 'triggerPrice' });

  const { trackTpslSettingConfirmClick } = useBunja(tpslModalSegmentBunja);
  const { tpSlInfo, onCloseTimeTriggerTpslModal } = useTimeTriggerTpslModal();
  const [, deleteTpSl] = useDeleteTpSlLegacy();
  const { symbol, socketSymbol, apiSymbol } = useTimeTriggerTpslBaseInfo();

  const currentPosition = positions?.[symbol]?.find((p) => p.slot === tpSlInfo?.slot);
  const pricePrecision = apiSymbol?.pricePrecision || 2;
  const side = currentPosition?.side;
  const leverage = currentPosition?.initLeverage;
  const liqPrice = currentPosition?.liquidationPrice || '0'; // 없을땐 0 으로 취급;
  const prevTriggerPrice = tpSlInfo?.tpSlType
    ? currentPosition?.[tpSlInfo?.tpSlType === 'tp' ? 'takeProfit' : 'stopLoss']
    : '0';
  const isExistPrevTriggerPrice = Number(prevTriggerPrice) > 0;
  const referencePrice = getTickerDisplayPriceOrDefault(socketSymbol);
  const entryPrice = currentPosition?.avgEntryPrice || '0';

  const estPnlInfo = calcPositionEstUnrealizedPnl(currentPosition, currentTriggerPrice);

  const [isLoading, onSubmit] = useHandleSubmit(handleSubmit, async ({ triggerPrice }) => {
    if (!tpSlInfo || !side || !referencePrice) return;

    if (!triggerPrice) {
      // triggerPrice 가 없는 경우 기존에 셋팅된 trigger price 가 있어서 제거할 목적으로 사용시
      if (isExistPrevTriggerPrice) {
        deleteTpSlAlert(tpSlInfo, () => {
          deleteTpSl(tpSlInfo.side, tpSlInfo.tpSlType, tpSlInfo.symbol, tpSlInfo.slot);
          onCloseTimeTriggerTpslModal();
        });
        return;
      }
      onCloseTimeTriggerTpslModal();
      return;
    }

    const upsertTpSl = async () => {
      try {
        if (!account) throw new Error(transAssets('There_was_an_unexpected_error_please_try_again_later'));
        const tpSlSide = side === 'Long' ? 'Short' : 'Long';

        switch (account.tradeMode) {
          case 'MultiplePositions':
            await postOrderApi(symbol, {
              action: 'Close',
              side: tpSlSide,
              slot: tpSlInfo.slot,
              requestId: uuid(),
              timestamp: dateToEpochString(new Date()),
              orders: [
                {
                  triggerDirection: TpSlTriggerDirectionMapper[side][tpSlInfo.tpSlType],
                  triggerPrice,
                },
              ],
              ...refPositionTimes[symbol],
              attribution,
            });
            break;

          case 'OneWay':
            await postOneWayTpslApi(symbol, {
              requestId: uuid(),
              timestamp: dateToEpochString(new Date()),
              side: tpSlSide,
              triggerPrice,
              triggerDirection: TpSlTriggerDirectionMapper[side][tpSlInfo.tpSlType],
              attribution,
            });
            break;

          default:
            account.tradeMode satisfies never;
            throw new Error('Unexpected tradeMode');
        }

        snackbar.success({ message: transPosition('Position_update_toast') });
        onCloseTimeTriggerTpslModal();
      } catch (e) {
        tpSlErrorHandler(e, onCloseTimeTriggerTpslModal);
      }
    };

    trackTpslSettingConfirmClick();
    if (checkShowLiquidationAlert(side, triggerPrice, liqPrice)) {
      liquidationAlert(upsertTpSl);
      return;
    }
    await upsertTpSl();
  });

  useEffect(() => {
    if (tpSlInfo) {
      if (initPosition.current) return;
      reset(
        {
          side,
          positionType: tpSlInfo.positionType,
          tpSlType: tpSlInfo.tpSlType,
          leverage,
          // 상황에 맞춰서 side 에 맞춰서 넣어주는것이 맞긴하지만
          // validation 시에 맞는 키 값만 보기 때문에 동일하게 넣어주어도 될 듯
          liqPriceLong: liqPrice,
          liqPriceShort: liqPrice,
          price: referencePrice,
        },
        { keepValues: false },
      );
      const basePrice = isExistPrevTriggerPrice ? prevTriggerPrice : entryPrice;
      setValue('basePrice', basePrice);
      setValue('entryPrice', entryPrice);
      setValue('triggerPrice', basePrice, {
        shouldDirty: true,
        shouldValidate: true,
      });
      initPosition.current = true;
    } else {
      reset();
      initPosition.current = false;
    }
  }, [
    referencePrice,
    leverage,
    liqPrice,
    reset,
    side,
    prevTriggerPrice,
    tpSlInfo,
    setValue,
    isExistPrevTriggerPrice,
    entryPrice,
  ]);

  useEffect(() => {
    setValue('price', referencePrice);
    trigger('triggerPrice');
  }, [referencePrice, setValue, trigger]);

  useEffect(() => {
    if (initPosition.current && !currentPosition) {
      onCloseTimeTriggerTpslModal();
    }
  }, [currentPosition, onCloseTimeTriggerTpslModal]);

  if (!tpSlInfo || !currentPosition) return <LoadingSpinner />;

  return (
    <form onSubmit={onSubmit}>
      <SymbolInfo
        symbol={tpSlInfo.symbol}
        displayPrice={referencePrice}
        pricePrecision={pricePrecision}
        side={currentPosition.side}
        leverage={currentPosition.initLeverage}
        marginType={currentPosition.marginType}
      />
      <TriggerPriceInput entryPrice={entryPrice} pricePrecision={pricePrecision} />
      <PriceInfos
        positionType={tpSlInfo.positionType}
        price={currentPosition.avgEntryPrice}
        unrealizedPnl={estPnlInfo.estUnrealizedPnl}
        unrealizedPnlPercent={estPnlInfo.estUnrealizedPnlPct}
        liquidationPrice={currentPosition.liquidationPrice}
        pricePrecision={pricePrecision}
      />
      <TriggerPriceRate
        side={currentPosition.side}
        tpSlType={tpSlInfo.tpSlType}
        entryPrice={entryPrice}
        pricePrecision={pricePrecision}
      />
      <ConfirmButton isLoading={isLoading} />
    </form>
  );
};

export default OpenPosition;
